import React, { useState, useRef, useEffect, useCallback } from 'react';
import './CustomerSelect.css';

interface Option {
  value: string;
  label: string;
}

interface CustomerSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  required?: boolean;
  id?: string;
  className?: string;
  /** Allow clearing to empty (for filter dropdowns). When true, shows "All" option. */
  allowEmpty?: boolean;
  emptyLabel?: string;
}

const CustomerSelect: React.FC<CustomerSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Search customers...',
  required = false,
  id,
  className = '',
  allowEmpty = false,
  emptyLabel = 'All Customers',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = search
    ? options.filter(o =>
        o.label.toLowerCase().includes(search.toLowerCase()) ||
        o.value.toLowerCase().includes(search.toLowerCase())
      )
    : options;

  const selectedLabel = options.find(o => o.value === value)?.label || '';

  const handleSelect = useCallback((val: string) => {
    onChange(val);
    setSearch('');
    setIsOpen(false);
  }, [onChange]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearch('');
    } else if (e.key === 'Enter' && filtered.length === 1) {
      e.preventDefault();
      handleSelect(filtered[0].value);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`customer-select-typeahead ${className} ${isOpen ? 'open' : ''}`}
      id={id}
    >
      {/* Hidden input for form validation */}
      {required && (
        <input
          type="text"
          value={value}
          required
          tabIndex={-1}
          style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
          onChange={() => {}}
        />
      )}

      <div
        className="customer-select-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={value ? 'selected-value' : 'placeholder-value'}>
          {value ? selectedLabel : placeholder}
        </span>
        <span className="customer-select-arrow">{isOpen ? '\u25B2' : '\u25BC'}</span>
      </div>

      {isOpen && (
        <div className="customer-select-dropdown">
          <input
            ref={inputRef}
            type="text"
            className="customer-select-search"
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <div className="customer-select-options">
            {allowEmpty && (
              <div
                className={`customer-select-option ${value === '' ? 'selected' : ''}`}
                onClick={() => handleSelect('')}
              >
                {emptyLabel}
              </div>
            )}
            {filtered.length === 0 ? (
              <div className="customer-select-no-results">No matches</div>
            ) : (
              filtered.map(option => (
                <div
                  key={option.value}
                  className={`customer-select-option ${option.value === value ? 'selected' : ''}`}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSelect;
